import type { App } from 'vue'
import { OpenAPI } from '@/api'
import { newRequestId } from '@/utils/uuid'
import { getBuildSha } from '@/version'
import type { ClientEvent, ClientEventContext } from './events'
import { sanitizeEvent, type SafeEventFields } from './sanitize'
import { parseTraceparent, type BrowserTraceContext } from './traceContext'

const MAX_BATCH_EVENTS = 20
const MAX_BATCH_BYTES = 64 * 1024
const MAX_QUEUED_EVENTS = 40
const MAX_QUEUED_BYTES = 128 * 1024
const IDLE_FLUSH_MS = 1_000
const requestIdPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

let queue: SafeEventFields[] = []
let idleTimer: ReturnType<typeof setTimeout> | null = null
let flushPromise: Promise<void> | null = null
let invokingFetch = false
let pagehideKeepalive = false
let lastApiRequestId: string | null = null
let lastApiTraceContext: BrowserTraceContext | null = null
let contextProvider: (() => ClientEventContext) | null = null
let pagehideInstalled = false

function clearIdleTimer(): void {
  if (idleTimer !== null) clearTimeout(idleTimer)
  idleTimer = null
}

function scheduleIdleFlush(): void {
  clearIdleTimer()
  idleTimer = setTimeout(() => {
    idleTimer = null
    void flushClientEvents()
  }, IDLE_FLUSH_MS)
}

function serializedBatch(events: SafeEventFields[]): string {
  return JSON.stringify({ events })
}

function takeBatch(): SafeEventFields[] {
  const batch: SafeEventFields[] = []
  while (queue.length > 0 && batch.length < MAX_BATCH_EVENTS) {
    const candidate = queue[0]
    if (!candidate) break
    const next = [...batch, candidate]
    if (new globalThis.TextEncoder().encode(serializedBatch(next)).byteLength > MAX_BATCH_BYTES) {
      break
    }
    batch.push(candidate)
    queue.shift()
  }
  return batch
}

async function sendBatch(events: SafeEventFields[], keepalive: boolean): Promise<void> {
  if (events.length === 0) return
  try {
    invokingFetch = true
    const request = fetch(`${OpenAPI.BASE}/client-events`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: serializedBatch(events),
      keepalive,
    })
    invokingFetch = false
    await request
  } catch {
    // Diagnostic transport is best-effort and never retries a product action.
  } finally {
    invokingFetch = false
  }
}

function ensurePagehideListener(): void {
  if (pagehideInstalled) return
  window.addEventListener('pagehide', handlePagehide)
  pagehideInstalled = true
}

function handlePagehide(): void {
  clearIdleTimer()
  void flushClientEvents(true)
}

export function configureClientEventContext(provider: () => ClientEventContext): void {
  contextProvider = provider
  ensurePagehideListener()
}

export function captureApiRequestId(response: Pick<globalThis.Response, 'headers'>): void {
  const value = response.headers?.get('X-Request-ID')
  if (value && requestIdPattern.test(value)) lastApiRequestId = value
}

export function getLastApiRequestId(): string | null {
  return lastApiRequestId
}

export function captureApiTraceparent(traceparent: string | null): void {
  lastApiTraceContext = parseTraceparent(traceparent)
}

export function reportClientEvent(event: ClientEvent): void {
  if (invokingFetch) return
  try {
    const context = contextProvider?.() ?? {}
    const candidate = {
      ...event,
      ...context,
      schema_version: 1,
      timestamp: new Date().toISOString(),
      service: 'web',
      release_sha: getBuildSha() || 'local',
      event_id: newRequestId(),
      ...(lastApiRequestId ? { request_id: lastApiRequestId } : {}),
      ...(lastApiTraceContext
        ? {
            trace_id: lastApiTraceContext.traceId,
            span_id: lastApiTraceContext.spanId,
            trace_sampled: lastApiTraceContext.sampled,
          }
        : {}),
    }
    const sanitized = sanitizeEvent(candidate, 'cloud_logging')
    if (!sanitized) return
    if (queue.length >= MAX_QUEUED_EVENTS) return
    if (
      new globalThis.TextEncoder().encode(serializedBatch([...queue, sanitized])).byteLength >
      MAX_QUEUED_BYTES
    ) {
      return
    }
    queue.push(sanitized)
    if (queue.length >= MAX_BATCH_EVENTS || event.severity === 'CRITICAL') {
      clearIdleTimer()
      void flushClientEvents()
    } else {
      scheduleIdleFlush()
    }
  } catch {
    // Telemetry must never create a second user-facing failure.
  }
}

export function flushClientEvents(keepalive = false): Promise<void> {
  if (keepalive) pagehideKeepalive = true
  if (flushPromise) return flushPromise
  clearIdleTimer()
  flushPromise = (async () => {
    while (queue.length > 0) {
      const batch = takeBatch()
      if (batch.length === 0) {
        queue = []
        return
      }
      await sendBatch(batch, keepalive || pagehideKeepalive)
    }
  })().finally(() => {
    flushPromise = null
    pagehideKeepalive = false
    if (queue.length > 0) scheduleIdleFlush()
  })
  return flushPromise
}

function errorFields(value: unknown, fallbackType: string): Partial<ClientEvent> {
  if (!(value instanceof Error)) return { exception_type: fallbackType }
  const type = /^[A-Za-z_][A-Za-z0-9_.]{0,127}$/.test(value.name) ? value.name : fallbackType
  return { exception_type: type }
}

export function installClientErrorHandlers(app: App): () => void {
  const previousVueHandler = app.config.errorHandler
  app.config.errorHandler = (error) => {
    reportClientEvent({
      event_name: 'browser_error',
      severity: 'ERROR',
      error_code: 'internal_error',
      operation: 'vue_error_handler',
      ...errorFields(error, 'VueError'),
    })
  }
  const onWindowError = (event: Event) => {
    reportClientEvent({
      event_name: 'browser_error',
      severity: 'ERROR',
      error_code: 'internal_error',
      operation: 'window_error',
      ...errorFields((event as Event & { error?: unknown }).error, 'WindowError'),
    })
  }
  const onUnhandledRejection = (event: Event) => {
    reportClientEvent({
      event_name: 'browser_unhandled_rejection',
      severity: 'ERROR',
      error_code: 'internal_error',
      operation: 'unhandled_rejection',
      ...errorFields((event as Event & { reason?: unknown }).reason, 'UnhandledRejection'),
    })
  }
  window.addEventListener('error', onWindowError)
  window.addEventListener('unhandledrejection', onUnhandledRejection)
  return () => {
    app.config.errorHandler = previousVueHandler
    window.removeEventListener('error', onWindowError)
    window.removeEventListener('unhandledrejection', onUnhandledRejection)
  }
}

export function resetClientEventReporter(): void {
  resetClientEventSession()
  contextProvider = null
  if (pagehideInstalled) window.removeEventListener('pagehide', handlePagehide)
  pagehideInstalled = false
}

export function resetClientEventSession(): void {
  clearIdleTimer()
  queue = []
  pagehideKeepalive = false
  lastApiRequestId = null
  lastApiTraceContext = null
}
